import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { getThemeLang, setThemeLang } from "src/app/core/utils/util";
import { environment } from "src/environments/environment";
import { BehaviorSubject, catchError, of } from "rxjs";
import { Title } from "@angular/platform-browser";
import { ApiService } from "../data/services/api.service";
import defaultString from "../../assets/i18n/en.json";
import { Helper } from "src/app/shared/helper";
import { apiColletions } from "../../app/core/constant/api-collection";
import { LangService } from "./lang.service";

export class Language {
  code!: string;
  direction!: string;
  label!: string;
  shorthand!: string;
}

@Injectable({ providedIn: "root" })
export class LanguageService {
  LANGUAGE_URL = environment.API_URL;
  defaultLang = 'en';

  isSingleLang = false;
  renderer: Renderer2;
  defaultLanguage = getThemeLang();
  supportedLanguages: Language[] = [];
  private languages = new BehaviorSubject<any>(null);
  languagesObservable = this.languages.asObservable();

  constructor(
    private translate: TranslateService,
    private rendererFactory: RendererFactory2,
    private helper: Helper,
    private langService: LangService,
    private titleService: Title,
    private apiService: ApiService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Load the list of active languages from the API.
   * Does NOT reset the stored language preference.
   */
  configLanguages(): Promise<any> {
    return new Promise((resolve) => {
      this.langService.getLanguageList({ isActive: true }).subscribe((response: any) => {
        if (response && response.length) {
          response.forEach((_lang: any) => {
            const langDirection = _lang.code === 'ar' ? "rtl" : "ltr";
            this.supportedLanguages.push({
              code: _lang.code,
              direction: langDirection,
              label: _lang.name,
              shorthand: _lang.code
            });
          });

          // Check if stored language is in the supported list
          const storedLang = localStorage.getItem("theme_lang");
          const isSupported = this.supportedLanguages.some(l => l.code === storedLang);

          if (!storedLang) {
            // First visit — set default language
            setThemeLang(this.defaultLang, "ltr");
            console.log('[LanguageService] No stored language, setting default:', this.defaultLang);
          } else if (!isSupported) {
            // Stored language is not in the active list — keep it anyway
            // (user chose it, but admin may have deactivated it)
            console.log('[LanguageService] Stored lang', storedLang, 'not in active list, keeping preference');
          }

          setTimeout(() => {
            this.languages.next({});
          });
          resolve(true);
        } else {
          this.supportedLanguages.push({
            code: this.defaultLang,
            direction: "ltr",
            label: "English",
            shorthand: this.defaultLang
          });
          this.languages.next({});
          resolve(true);
        }
      });
    });
  }

  /**
   * Main initialization — called once from AppComponent.ngOnInit().
   *
   * Flow:
   *   1. Register English as the hardcoded fallback (setTranslation + setDefaultLang)
   *   2. Load supported languages from DB
   *   3. Determine the active language from localStorage
   *   4. Try to load that language's strings from the API (DB strings)
   *   5. Call translate.use(lang) — this also triggers TranslateHttpLoader
   *      to load assets/i18n/{lang}.json, which provides the full translation.
   *      If API strings were loaded in step 4, they override the JSON file.
   */
  async init(): Promise<void> {
    // 1. English is always the hardcoded fallback
    this.translate.setTranslation(this.defaultLang, defaultString);
    this.translate.setDefaultLang(this.defaultLang);

    // 2. Load supported languages from DB
    await this.configLanguages();

    // 3. Determine the active language from localStorage
    const storedLang = localStorage.getItem("theme_lang") || this.defaultLang;
    this.helper.cuurentLang = storedLang;
    this.defaultLanguage = storedLang;

    console.log('[LanguageService] init: active language =', storedLang);

    // 4. Try to load DB strings for the active language
    await this.setServerLanguage();

    // 5. Activate the language — this triggers TranslateHttpLoader for assets/i18n/{lang}.json
    //    If setServerLanguage already loaded translations via setTranslation, those take precedence.
    this.translate.use(this.defaultLanguage);

    // 6. Set body direction class
    this.renderer.removeClass(document.body, "rtl");
    this.renderer.removeClass(document.body, "ltr");

    if (this.defaultLanguage === "ar") {
      this.renderer.addClass(document.body, "rtl");
    } else {
      this.renderer.addClass(document.body, "ltr");
    }

    this.setPanelName();
  }

  /**
   * Load translations from the API (DB strings) for the current language.
   * If the API returns data, we merge it into the translate service.
   * If the API returns empty, we do NOT override — let the JSON file handle it.
   */
  setServerLanguage(): Promise<any> {
    return new Promise((resolve) => {
      const lang = this.helper.cuurentLang;
      const parameters = {
        stringType: this.helper.StringType.PANEL_STRINGS,
        code: lang,
        isShowErrorToast: false
      };

      this.apiService
        .get({ url: apiColletions.strings, parameters: parameters })
        .pipe(
          catchError((err) => {
            console.warn('[LanguageService] API error loading strings for', lang, err);
            resolve(true);
            return of(null);
          })
        )
        .subscribe((res: any) => {
          if (res && res.data && res.data.data && res.data.data[0]) {
            const languageStrings = res.data.data[0].data;
            // Merge API strings — these take priority over the JSON file
            this.translate.setTranslation(lang, languageStrings, true);
            console.log('[LanguageService] Loaded DB strings for', lang);
          } else {
            // No DB strings — let TranslateHttpLoader load assets/i18n/{lang}.json
            // Do NOT set English defaults here — that would overwrite the JSON translations!
            console.log('[LanguageService] No DB strings for', lang, '— relying on assets/i18n/' + lang + '.json');
          }
          resolve(true);
        });
    });
  }

  setPanelName(): void {
    setTimeout(() => {
      this.titleService.setTitle(this.translate.instant("app-title.customer-panel"));
    }, 500);
  }

  checkForDirectionChange(): void {
    this.renderer.removeClass(document.body, "ltr");
    this.renderer.removeClass(document.body, "rtl");
    this.renderer.addClass(document.body, this.direction);
    this.renderer.setAttribute(document.documentElement, "direction", this.direction);
  }

  /**
   * Called when the user changes language (e.g. from footer dropdown).
   * Persists the choice, loads translations, and updates the UI.
   */
  set language(lang: string) {
    let language = lang || getThemeLang();

    const isSupportedLanguage = this.supportedLanguages.some(item => item.code === language);
    if (!isSupportedLanguage) {
      console.warn('[LanguageService] Unsupported language:', language, '— falling back to', this.defaultLang);
      language = this.defaultLang;
    }

    // Persist the choice FIRST so it survives a reload
    setThemeLang(language, this.direction);
    localStorage.setItem('theme_lang', language);
    this.helper.cuurentLang = language;
    this.defaultLanguage = language;

    // Load DB strings for the new language, then activate it
    const parameters = {
      stringType: this.helper.StringType.PANEL_STRINGS,
      code: language,
      isShowErrorToast: false
    };

    this.apiService
      .get({ url: apiColletions.strings, parameters: parameters })
      .pipe(catchError(() => of(null)))
      .subscribe((res: any) => {
        if (res && res.data && res.data.data && res.data.data[0]) {
          this.translate.setTranslation(language, res.data.data[0].data, true);
        }
        // Activate the language — TranslateHttpLoader will also load assets/i18n/{lang}.json
        this.translate.use(language);
        this.checkForDirectionChange();
        this.setPanelName();
        this.languages.next({ lang: language });
      });
  }

  get language(): string {
    return this.translate.currentLang;
  }

  get languageShorthand(): string {
    const currentLang = this.supportedLanguages.find(item => item.code === this.translate.currentLang);
    return currentLang ? currentLang.shorthand : this.defaultLang;
  }

  get direction(): string {
    const currentDirection = this.supportedLanguages.find(item => item.code === this.translate.currentLang);
    return currentDirection ? currentDirection.direction : "ltr";
  }

  get languageLabel(): string {
    return this.supportedLanguages?.find(item => item.code === this.translate.currentLang)?.label ?? this.defaultLang;
  }

  get selectedlanguageIndex(): number {
    return this.supportedLanguages.findIndex(item => item.code === this.translate.currentLang);
  }
}
